import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Building2, AlertTriangle, TrendingUp, Globe2, RefreshCw } from 'lucide-react';
import { getOverviewStats, getTopRisk } from '../api/client';
import type { RiskGrade } from '../types';

function StatCard({
  title,
  value,
  icon: Icon,
  href,
}: {
  title: string;
  value: string | number;
  icon: React.ElementType;
  href?: string;
}) {
  const content = (
    <div className="card p-6">
      <div className="flex items-center gap-4">
        <div className="p-3 bg-primary-50 rounded-lg">
          <Icon className="h-6 w-6 text-primary-600" />
        </div>
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );

  if (href) {
    return <Link to={href}>{content}</Link>;
  }
  return content;
}

function GradeBar({ grade, count, total }: { grade: RiskGrade; count: number; total: number }) {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  const colors = {
    A: 'bg-green-500',
    B: 'bg-lime-500',
    C: 'bg-yellow-500',
    D: 'bg-orange-500',
    F: 'bg-red-500',
  };

  return (
    <div className="flex items-center gap-3">
      <span className="w-8 font-semibold text-gray-700">{grade}</span>
      <div className="flex-1 bg-gray-100 rounded-full h-4">
        <div
          className={`${colors[grade]} h-4 rounded-full transition-all`}
          style={{ width: `${percentage}%` }}
        />
      </div>
      <span className="w-24 text-right text-sm text-gray-500">
        {count > 0 ? `${count.toLocaleString()} (${percentage.toFixed(1)}%)` : '0'}
      </span>
    </div>
  );
}

export default function Dashboard() {
  const { data: stats, isLoading: statsLoading, error: statsError, refetch: refetchStats } = useQuery({
    queryKey: ['stats', 'overview'],
    queryFn: getOverviewStats,
    retry: 2,
  });

  const { data: topRisk, isLoading: topRiskLoading, error: topRiskError } = useQuery({
    queryKey: ['risk-scores', 'top'],
    queryFn: () => getTopRisk(10),
    retry: 2,
  });

  if (statsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (statsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <AlertTriangle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            Unable to load dashboard
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            {statsError instanceof Error ? statsError.message : 'Failed to fetch statistics'}
          </p>
          <button
            onClick={() => refetchStats()}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const totalGraded = stats
    ? Object.values(stats.grade_distribution).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          Privacy Risk Dashboard
        </h1>
        <p className="mt-2 text-gray-600">
          Overview of EU financial institution privacy risk analysis
        </p>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Institutions"
          value={stats?.total_institutions.toLocaleString() || '0'}
          icon={Building2}
          href="/search"
        />
        <StatCard
          title="Risk Scored"
          value={stats?.scored_institutions.toLocaleString() || '0'}
          icon={TrendingUp}
        />
        <StatCard
          title="Enforcement Issues"
          value={stats?.total_enforcement_issues.toLocaleString() || '0'}
          icon={AlertTriangle}
        />
        <StatCard
          title="Countries Covered"
          value={Object.keys(stats?.institutions_by_country || {}).length}
          icon={Globe2}
        />
      </div>

      {/* Two columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Grade distribution */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Risk Grade Distribution
          </h2>
          {stats && (
            <div className="space-y-3">
              {(['A', 'B', 'C', 'D', 'F'] as RiskGrade[]).map((grade) => (
                <GradeBar
                  key={grade}
                  grade={grade}
                  count={stats.grade_distribution[grade] || 0}
                  total={totalGraded}
                />
              ))}
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">
              Average score: {Number(stats?.average_risk_score || 0).toFixed(1)}/100
              {totalGraded > 0 && ` | ${totalGraded.toLocaleString()} institutions scored`}
            </p>
          </div>
        </div>

        {/* Top risk institutions */}
        <div className="card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Highest Risk Institutions
          </h2>
          {topRiskLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
            </div>
          ) : topRiskError ? (
            <p className="text-sm text-gray-500">Unable to load top risk data.</p>
          ) : (
            <div className="space-y-3">
              {topRisk?.slice(0, 5).map((inst, i) => (
                <Link
                  key={inst.lei}
                  to={`/institution/${inst.lei}`}
                  className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <span className="text-sm text-gray-400 w-4">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {inst.legal_name}
                    </p>
                  </div>
                  <span
                    className={`badge badge-${inst.overall_grade.toLowerCase()}`}
                  >
                    {inst.overall_grade}
                  </span>
                  <span className="text-sm text-gray-500">
                    {Number(inst.overall_score).toFixed(0)}
                  </span>
                </Link>
              ))}
            </div>
          )}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <Link
              to="/search?sort=risk&order=desc"
              className="text-sm text-primary-600 hover:text-primary-700"
            >
              View all high-risk institutions &rarr;
            </Link>
          </div>
        </div>
      </div>

      {/* Institution types */}
      <div className="card p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Institutions by Type
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {stats &&
            Object.entries(stats.institutions_by_type)
              .sort(([, a], [, b]) => b - a)
              .map(([type, count]) => (
                <Link
                  key={type}
                  to={`/search?type=${type}`}
                  className="p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <p className="text-sm text-gray-500 truncate">{type.replace(/_/g, ' ')}</p>
                  <p className="text-xl font-semibold text-gray-900">
                    {count.toLocaleString()}
                  </p>
                </Link>
              ))}
        </div>
      </div>
    </div>
  );
}
